import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const PageTreeNode = ({ 
  page, 
  level = 0, 
  onToggle, 
  onSelect, 
  selectedPages = [], 
  onDragStart,
  onDragOver,
  onDrop,
  onEdit,
  onDuplicate,
  onDelete
}) => {
  const [isExpanded, setIsExpanded] = useState(level === 0);
  const [showActions, setShowActions] = useState(false);

  const isSelected = selectedPages?.includes(page?.id);
  const hasChildren = page?.children && page?.children?.length > 0;

  const handleToggle = (e) => {
    e?.stopPropagation();
    setIsExpanded(!isExpanded);
    if (onToggle) onToggle(page?.id);
  };

  const handleSelect = (e) => {
    e?.stopPropagation();
    if (onSelect) onSelect(page?.id);
  };

  const getStatusColor = (status) => {
    const colors = {
      published: 'text-success bg-success/10',
      draft: 'text-muted-foreground bg-muted',
      scheduled: 'text-warning bg-warning/10',
      archived: 'text-error bg-error/10'
    };
    return colors?.[status] || colors?.draft;
  };

  const getStatusIcon = (status) => {
    const icons = {
      published: 'CheckCircle2',
      draft: 'FileEdit',
      scheduled: 'Clock',
      archived: 'Archive'
    };
    return icons?.[status] || 'FileEdit';
  };

  return (
    <div className="w-full">
      <div
        draggable
        onDragStart={(e) => onDragStart && onDragStart(e, page)}
        onDragOver={(e) => onDragOver && onDragOver(e)}
        onDrop={(e) => onDrop && onDrop(e, page)}
        className={`group flex items-center gap-2 md:gap-3 p-2 md:p-3 rounded-lg border border-border hover:bg-muted/50 transition-all duration-200 cursor-pointer ${
          isSelected ? 'bg-primary/5 border-primary' : 'bg-card'
        }`}
        style={{ marginLeft: `${level * 16}px` }}
        onMouseEnter={() => setShowActions(true)}
        onMouseLeave={() => setShowActions(false)}
      >
        <input
          type="checkbox"
          checked={isSelected}
          onChange={handleSelect}
          className="w-4 h-4 rounded border-border text-primary focus:ring-2 focus:ring-primary focus:ring-offset-0"
          onClick={(e) => e?.stopPropagation()}
        />

        {hasChildren && (
          <button
            onClick={handleToggle}
            className="w-6 h-6 flex items-center justify-center rounded hover:bg-muted transition-colors"
          >
            <Icon
              name={isExpanded ? 'ChevronDown' : 'ChevronRight'}
              size={16}
              className="text-muted-foreground"
            />
          </button>
        )}

        {!hasChildren && <div className="w-6" />}

        <div className="w-10 h-10 md:w-12 md:h-12 rounded-lg overflow-hidden flex-shrink-0 bg-muted">
          <Image
            src={page?.thumbnail}
            alt={page?.thumbnailAlt}
            className="w-full h-full object-cover"
          />
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <h4 className="font-medium text-sm md:text-base text-foreground truncate">
              {page?.title}
            </h4>
            <span className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor(page?.status)}`}>
              <Icon name={getStatusIcon(page?.status)} size={12} />
              <span className="hidden sm:inline">{page?.status}</span>
            </span>
          </div>
          <div className="flex items-center gap-2 md:gap-4 text-xs text-muted-foreground">
            <span className="truncate">{page?.url}</span>
            <span className="hidden md:inline whitespace-nowrap">•</span>
            <span className="hidden md:inline whitespace-nowrap">{page?.author}</span>
            <span className="whitespace-nowrap">•</span>
            <span className="whitespace-nowrap">{page?.lastModified}</span>
          </div>
        </div>

        <div className="flex items-center gap-2 md:gap-4 flex-shrink-0">
          <div className="hidden lg:flex items-center gap-3 text-xs text-muted-foreground">
            <div className="flex items-center gap-1">
              <Icon name="Eye" size={14} />
              <span className="whitespace-nowrap">{page?.views}</span>
            </div>
            <div className="flex items-center gap-1">
              <Icon name="TrendingUp" size={14} />
              <span className="whitespace-nowrap">{page?.seoScore}%</span>
            </div>
          </div>

          <div className={`flex items-center gap-1 transition-opacity duration-200 ${showActions ? 'opacity-100' : 'opacity-0 lg:opacity-0'}`}>
            <button
              onClick={(e) => {
                e?.stopPropagation();
                onEdit && onEdit(page);
              }}
              className="w-8 h-8 flex items-center justify-center rounded hover:bg-muted transition-colors"
              title="Edit page"
            >
              <Icon name="Edit2" size={16} className="text-muted-foreground hover:text-foreground" />
            </button>
            <button
              onClick={(e) => {
                e?.stopPropagation();
                onDuplicate && onDuplicate(page);
              }}
              className="w-8 h-8 flex items-center justify-center rounded hover:bg-muted transition-colors"
              title="Duplicate page"
            >
              <Icon name="Copy" size={16} className="text-muted-foreground hover:text-foreground" />
            </button>
            <button
              onClick={(e) => {
                e?.stopPropagation();
                onDelete && onDelete(page);
              }}
              className="w-8 h-8 flex items-center justify-center rounded hover:bg-error/10 transition-colors"
              title="Delete page"
            >
              <Icon name="Trash2" size={16} className="text-muted-foreground hover:text-error" />
            </button>
          </div>
        </div>
      </div>
      {isExpanded && hasChildren && (
        <div className="mt-1 md:mt-2">
          {page?.children?.map((child) => (
            <PageTreeNode
              key={child?.id}
              page={child}
              level={level + 1}
              onToggle={onToggle}
              onSelect={onSelect}
              selectedPages={selectedPages}
              onDragStart={onDragStart}
              onDragOver={onDragOver}
              onDrop={onDrop}
              onEdit={onEdit}
              onDuplicate={onDuplicate}
              onDelete={onDelete}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default PageTreeNode;